/** Marketing / landing pages hidden in the Capacitor app. */
export const NATIVE_MARKETING_PATHS = new Set([
  "/",
  "/hur-det-fungerar",
  "/salj-bilen",
  "/kop-bil",
  "/how-it-works",
  "/sell-car",
  "/buy-car",
]);

export const NATIVE_BOTTOM_NAV_HIDDEN = new Set([
  "/verify-email",
  "/accept-terms",
  "/reset-password",
  "/car-valuation-embed",
  "/listings-embed",
]);

/** Default cold-start route for unauthenticated native users. */
export const NATIVE_GUEST_HOME = "/auctions";

/** Paths where logged-out users see the guest bottom tab bar. */
export const NATIVE_GUEST_BOTTOM_NAV_PATHS = new Set([
  "/auth",
  "/auctions",
  "/auctions/all",
]);

export function normalizePath(pathname: string): string {
  const p = pathname.replace(/\/+$/, "");
  return p || "/";
}

export function isMarketingPath(pathname: string): boolean {
  return NATIVE_MARKETING_PATHS.has(normalizePath(pathname));
}

export function shouldHideBottomNav(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (NATIVE_BOTTOM_NAV_HIDDEN.has(path)) return true;
  if (path === "/auctions/all") return false;
  if (/^\/auctions\/[^/]+\/[^/]+$/.test(path)) return true;
  if (/^\/admin\/leads\/[^/]+/.test(path)) return true;
  return false;
}

/** Guest tab bar on browse + auth entry (not listing detail). */
export function shouldShowGuestBottomNav(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (shouldHideBottomNav(path)) return false;
  if (NATIVE_GUEST_BOTTOM_NAV_PATHS.has(path)) return true;
  if (isNativeCampaignListPath(path)) return true;
  return false;
}

/** Car listing detail — edge-to-edge gallery, no bottom tab bar. */
/**
 * Web auctions browse shell: /auctions, /auctions/all, /auctions/:campaignId
 * — not listing detail /auctions/:campaignId/:listingId
 */
export function isAuctionsBrowseShellPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (path === "/auctions" || path === "/auctions/all") return true;
  const parts = path.split("/").filter(Boolean);
  return parts[0] === "auctions" && parts.length === 2;
}

export function isNativeListingDetailPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return /^\/auctions\/[^/]+\/[^/]+$/.test(path);
}

/** Campaign hub — /auctions/:campaignId (not listing detail). */
export function isNativeCampaignListPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return /^\/auctions\/[^/]+$/.test(path) && path !== "/auctions/all";
}

/** Hide floating profile on auctions hub, all listings, and campaign list. */
export function shouldHideNativeFloatingProfile(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (isNativeListingDetailPath(path)) return true;
  if (path === "/auctions" || path === "/auctions/all") return true;
  if (isNativeCampaignListPath(path)) return true;
  return false;
}

/** Tight top inset like CarDetails (no header / profile reserved space). */
export function shouldUseNativeCompactTop(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (path === "/auctions" || path === "/auctions/all") return true;
  if (isNativeCampaignListPath(path)) return true;
  return false;
}

export const NATIVE_PULL_REFRESH_PATHS = new Set([
  "/dashboard",
  "/auctions",
  "/auctions/all",
  "/orders",
  "/favorites",
  "/notifications",
  "/my-auctions",
  "/settings",
  "/car-test",
  "/admin/leadbox",
  "/admin/car-test",
  "/admin/feedback",
]);
